import { subYears } from "date-fns";
import { endOfWeek, startOfWeek, subDays, subMonths, subQuarters, subWeeks } from "date-fns/esm";
import {
	endOfDay,
	endOfMonth,
	endOfQuarter,
	endOfYear,
	startOfDay,
	startOfMonth,
	startOfQuarter,
	startOfYear,
} from "date-fns/esm/fp";
import { flow } from "lodash-es";
export const CUSTOM_RANGE_TYPE_ID = "time-range.preset.custom";
export interface TRange {
	name?: string;
	indexName?: string;
	rangeType: "day" | "week" | "month" | "quarter" | "year" | "flex";
	from: Date;
	to: Date;
	afterParse?(range: TRange): TRange;
	custom?: { add: (date: Date) => Date; sub: (date: Date) => Date };
}
export interface TRangePresets {
	[key: string]: TRange;
}
const TIME_RANGES = {
	day: <TRange>{
		indexName: "_00day",
		rangeType: "day",
		name: "time-range.preset.day",
		from: flow(startOfDay)(new Date()),
		to: flow(endOfDay)(new Date()),
	},
	week: <TRange>{
		indexName: "_01week",
		rangeType: "week",
		name: "time-range.preset.week",
		from: flow(startOfWeek)(new Date(), { weekStartsOn: 1 }),
		to: flow(endOfWeek)(new Date(), { weekStartsOn: 1 }),
	},
	tmonth: <TRange>{
		indexName: "_02tmonth",
		rangeType: "month",
		name: "time-range.preset.month",
		from: flow(startOfMonth)(new Date()),
		to: flow(endOfMonth)(new Date()),
		afterParse: (d: TRange) => {
			(d.from = flow(startOfMonth)(d.from)), (d.to = flow(endOfMonth)(d.to));
			return d;
		},
	},
	quarter: <TRange>{
		indexName: "_03quarter",
		rangeType: "quarter",
		name: "time-range.preset.quarter",
		from: flow(startOfQuarter)(new Date()),
		to: flow(endOfQuarter)(new Date()),
		afterParse: (d: TRange) => {
			(d.from = flow(startOfQuarter)(d.from)), (d.to = flow(endOfQuarter)(d.to));
			return d;
		},
	},
	year: <TRange>{
		indexName: "_04year",
		rangeType: "year",
		name: "time-range.preset.year",
		from: flow(startOfYear)(new Date()),
		to: flow(endOfYear)(new Date()),
		afterParse: (d: TRange) => {
			(d.from = flow(startOfYear)(d.from)), (d.to = flow(endOfYear)(d.to));
			return d;
		},
	},
	// month: <TRange>{
	//   indexName: '_2l30d',
	//   rangeType: 'month',
	//   name: 'time-range.preset.last-30-days',
	//   from: flow(subDays(29), startOfDay)(new Date()),
	//   to: flow(endOfDay)(new Date()),
	//   custom: {
	//     add: (date) => flow(addDays(30))(date),
	//     sub: (date) => flow(subDays(30))(date),
	//   },
	// },
	yesterday: <TRange>{
		indexName: "_05yesterday",
		rangeType: "day",
		name: "time-range.preset.yesterday",
		from: flow((s) => subDays(s, 1), startOfDay)(new Date()),
		to: flow((s) => subDays(s, 1), endOfDay)(new Date()),
		afterParse: (d: TRange) => {
			d.from = startOfDay(d.from);
			d.to = endOfDay(d.to);
			return d;
		},
	},
	lweek: <TRange>{
		indexName: "_06lweek",
		rangeType: "week",
		name: "time-range.preset.last-week",
		from: flow(
			(s) => subWeeks(s, 1),
			(s) => startOfWeek(s, { weekStartsOn: 1 })
		)(new Date()),
		to: flow(
			(s) => subWeeks(s, 1),
			(s) => endOfWeek(s, { weekStartsOn: 1 })
		)(new Date()),
	},
	lmonth: <TRange>{
		indexName: "_07lmonth",
		rangeType: "month",
		name: "time-range.preset.last-month",
		from: flow((s) => subMonths(s, 1), startOfMonth)(new Date()),
		to: flow((s) => subMonths(s, 1), endOfMonth)(new Date()),
		afterParse: (d: TRange) => {
			d.from = flow(startOfMonth)(d.from);
			d.to = flow(endOfMonth)(d.to);
			return d;
		},
	},
	lquarter: <TRange>{
		indexName: "_08quarter",
		rangeType: "quarter",
		name: "time-range.preset.last-quarter",
		from: flow((s) => subQuarters(s, 1), startOfQuarter)(new Date()),
		to: flow((s) => subQuarters(s, 1), endOfQuarter)(new Date()),
		afterParse: (d: TRange) => {
			d.from = flow(startOfQuarter)(d.from);
			d.to = flow(endOfQuarter)(d.to);
			return d;
		},
	},
	lyear: <TRange>{
		indexName: "_09year",
		rangeType: "year",
		name: "time-range.preset.last-year",
		from: flow((s) => subYears(s, 1), startOfYear)(new Date()),
		to: flow((s) => subYears(s, 1), endOfYear)(new Date()),
		afterParse: (d: TRange) => {
			(d.from = flow(startOfYear)(d.from)), (d.to = flow(endOfYear)(d.to));
			return d;
		},
	},
	untilToday: <TRange>{
		indexName: "_10until",
		rangeType: "flex",
		name: "time-range.preset.until-today",
		from: flow(startOfDay)(new Date()),
		to: flow(endOfDay)(new Date()),
		afterParse: (d: TRange) => {
			d.from = flow(startOfDay)(d.from);
			d.to = flow(endOfDay)(new Date());
			return d;
		},
	},
};
export const TIME_RANGE_CUSTOM_NAME = "time-range.preset.custom";
export const TIME_RANGE_DEFAULT_RANGES: TRangePresets & typeof TIME_RANGES = TIME_RANGES;
export const CUSTOM_TIME_RANGE_PRESET: TRange = {
	indexName: "_0custom",
	rangeType: "day",
	name: "time-range.preset.custom",
	from: flow(startOfDay)(new Date()),
	to: flow(endOfDay)(new Date()),
};
export function getTimeRangeKey(rangeName: string) {
	return Object.entries(TIME_RANGES).find(([key, value]) => value.name === rangeName)?.[0];
}
export function getTimeRangeByType(type: string) {
	return Object.entries(TIME_RANGES).find(([key, value]) => value.rangeType === type)?.[1];
}
export function getTimeRangeByKey(rangeName: string) {
	return Object.entries(TIME_RANGES).find(([key, value]) => value.name === rangeName)?.[1];
}
export function getTimeRangePresetMetadata(preset: TRange | undefined): Partial<TRange> {
	if (!preset) return {};
	const { from: _from, to: _to, ...metadata } = preset;
	return metadata;
}
export function getTimeRangeNameForStrategy(strategyType: string | null | undefined): string {
	if (!strategyType) return TIME_RANGE_CUSTOM_NAME;
	return getTimeRangeByType(strategyType)?.name ?? TIME_RANGE_CUSTOM_NAME;
}
export function resolveTimeRangePreset(range: TRange): TRange | undefined {
	if (range?.name) {
		const byName = getTimeRangeByKey(range.name);
		if (byName) return byName;
	}
	if (range?.rangeType) return getTimeRangeByType(range.rangeType);
	return undefined;
}
export function applyTimeRangeAfterParse(range: TRange): TRange {
	const preset = resolveTimeRangePreset(range);
	if (preset?.afterParse) return preset.afterParse({ ...range });
	return range;
}
